import type { PublicAuthSession } from "@no-busy-work/auth/protocol";
import { IncidentApprovalTransition, IncidentCommunicationTransition } from "@no-busy-work/ops-bridge-protocol";
import type {
  CreateIncidentRequest,
  IncidentApprovalTransitionRequest,
  IncidentCommunicationTransitionRequest,
  IncidentCreateOptions,
  IncidentDetail,
  IncidentListFilters,
  IncidentQueueItem,
  IncidentResponderAcknowledgementState,
  IncidentResponderAssignment,
  IncidentResponderKind,
  IncidentResponderRole,
  IncidentRecoveryAction,
  IncidentTask,
  IncidentTaskEscalationKind,
  IncidentTaskStatus,
  LocaleCode,
  PolicyClass,
  PublicToolCatalogEntry
} from "@no-busy-work/ops-bridge-protocol";

export type { CreateIncidentRequest, IncidentApprovalTransitionRequest, IncidentCommunicationTransitionRequest, IncidentDetail, IncidentListFilters, IncidentQueueItem, LocaleCode, PublicAuthSession };
export { IncidentApprovalTransition, IncidentCommunicationTransition };
export type {
  IncidentResponderAcknowledgementState,
  IncidentResponderAssignment,
  IncidentResponderKind,
  IncidentResponderRole,
  IncidentTask,
  IncidentTaskEscalationKind,
  IncidentTaskStatus
};

export const TAB_ID = {
  Overview: "overview",
  Impact: "impact",
  Timeline: "timeline",
  Actions: "actions",
  Comms: "comms",
  Evidence: "evidence",
  Resolution: "resolution"
} as const;

export type UiTone = "ok" | "warning" | "danger" | "neutral" | "critical";
export type TabId = (typeof TAB_ID)[keyof typeof TAB_ID];
export type GraphMode = "incident" | "fleet";
export type GraphHealth = "healthy" | "degraded" | "down" | "unknown";
export type IncidentQueueSort = "priority" | "customers" | "status" | "service";
export type OpsActionEndpointKind = "preview" | "approval_request" | "code_bridge_session";
export type AiQuestionKind =
  | "triage"
  | "customers"
  | "notice"
  | "blockers"
  | "tools"
  | "runbook"
  | "fingerprint"
  | "approvals"
  | "evidence"
  | "comms";

export interface PreviewFixtureHealth {
  readonly enabled: boolean;
  readonly mode: string;
  readonly environment: string;
  readonly tenantId: string;
  readonly envFlag: string;
  readonly labelKey: string;
  readonly seedSurfaces: readonly string[];
  readonly incidentIds: readonly string[];
  readonly createdIncidentIds: readonly string[];
}

export interface HealthResponse {
  readonly ok: boolean;
  readonly service?: string;
  readonly alias?: string;
  readonly appId?: string;
  readonly toolCatalog?: readonly ToolCatalogEntry[];
  readonly incidentCreateOptions?: IncidentCreateOptions;
  readonly previewFixtures?: PreviewFixtureHealth;
}

export type ToolCatalogEntry = PublicToolCatalogEntry;

export interface ToolRow {
  readonly id: string;
  readonly label: string;
  readonly provider: string;
  readonly owner: string;
  readonly environmentScope: readonly string[];
  readonly actionCount: number;
  readonly requiresApproval: boolean;
  readonly ready: boolean;
  readonly state: string;
}

export interface CreateIncidentForm {
  readonly rawSummary: string;
  readonly appId: string;
  readonly source: string;
  readonly serviceHint: string;
  readonly customServiceHint: string;
  readonly environment: string;
  readonly customerLocale: LocaleCode;
  readonly customerAudienceRef: string;
  readonly ownerRef: string;
  readonly severity: string;
}

export interface AiQuestionThread {
  readonly id: string;
  readonly kind: AiQuestionKind;
  readonly question: string;
  readonly answer: string;
  readonly followUps: readonly string[];
}

export interface IncidentQueueFilters {
  readonly appId: string;
  readonly environment: string;
  readonly severity: string;
  readonly status: string;
  readonly sort: IncidentQueueSort;
}

export interface OpsCenterState {
  readonly session: PublicAuthSession | null;
  readonly health: HealthResponse | null;
  readonly incident: IncidentDetail | null;
  readonly incidentQueue: readonly IncidentQueueItem[];
  readonly busy: boolean;
  readonly notice: string;
  readonly error: string;
  readonly operatorAccessCode: string;
  readonly activeTab: TabId;
  readonly graphMode: GraphMode;
  readonly graphFocusId: string;
  readonly timelinePosition: number;
  readonly toolDrawerOpen: boolean;
  readonly settingsOpen: boolean;
  readonly toolSearch: string;
  readonly aiOpen: boolean;
  readonly aiQuestionDraft: string;
  readonly aiThreads: readonly AiQuestionThread[];
  readonly createOpen: boolean;
  readonly create: CreateIncidentForm;
  readonly lookupIncidentId: string;
  readonly queueFilters: IncidentQueueFilters;
  readonly rootCause: string;
  readonly remediationProof: string;
  readonly verificationEvidence: string;
  readonly customerSafeSummary: string;
  readonly reopenReason: string;
}

export interface OpsCenterActions {
  readonly setOperatorAccessCode: (value: string) => void;
  readonly setCreateField: (name: keyof CreateIncidentForm, value: string) => void;
  readonly setLookupIncidentId: (value: string) => void;
  readonly setRootCause: (value: string) => void;
  readonly setRemediationProof: (value: string) => void;
  readonly setVerificationEvidence: (value: string) => void;
  readonly setCustomerSafeSummary: (value: string) => void;
  readonly setReopenReason: (value: string) => void;
  readonly setActiveTab: (tab: TabId) => void;
  readonly setGraphMode: (mode: GraphMode) => void;
  readonly setGraphFocusId: (nodeId: string) => void;
  readonly setTimelinePosition: (position: number) => void;
  readonly openToolDrawer: () => void;
  readonly closeToolDrawer: () => void;
  readonly openSettings: () => void;
  readonly closeSettings: () => void;
  readonly setToolSearch: (value: string) => void;
  readonly toggleAi: () => void;
  readonly openAi: () => void;
  readonly closeAi: () => void;
  readonly setAiQuestionDraft: (value: string) => void;
  readonly askAiQuestion: (question?: string, kind?: AiQuestionKind) => void;
  readonly setCreateOpen: (open: boolean) => void;
  readonly setQueueFilter: (name: keyof IncidentQueueFilters, value: string) => void;
  readonly loginWithOperatorAccessCode: () => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly createIncident: () => Promise<void>;
  readonly loadIncident: () => Promise<void>;
  readonly loadKnownIncident: (incidentId: string) => Promise<void>;
  readonly checkResolveEligibility: () => Promise<void>;
  readonly resolveIncident: () => Promise<void>;
  readonly reopenIncident: () => Promise<void>;
  readonly recordReadinessGate: (gateId: string) => Promise<void>;
  readonly transitionCommunication: (transition: IncidentCommunicationTransition) => Promise<void>;
  readonly transitionApproval: (transition: IncidentApprovalTransition) => Promise<void>;
  readonly invokeOpsAction: (actionId: string) => Promise<void>;
}

export interface ActionRow {
  readonly id: string;
  readonly proposalId: string;
  readonly gateId: string;
  readonly actionRef: string;
  readonly toolId: string | null;
  readonly kind: IncidentRecoveryAction["kind"];
  readonly label: string;
  readonly summary: string;
  readonly owner: string;
  readonly policyClass: PolicyClass;
  readonly policyLabel: string;
  readonly requiresApproval: boolean;
  readonly approvalState: IncidentRecoveryAction["approvalState"];
  readonly operationHash: string;
  readonly status: IncidentRecoveryAction["status"];
  readonly endpointKind: OpsActionEndpointKind;
  readonly path: string;
  readonly buttonLabel: string;
  readonly nextStep: string;
  readonly tone: UiTone;
}

export interface ResponderRow {
  readonly id: string;
  readonly responderRef: string;
  readonly label: string;
  readonly kind: IncidentResponderKind;
  readonly role: IncidentResponderRole;
  readonly roleLabel: string;
  readonly authorityClass: PolicyClass;
  readonly assignedTaskId: string | null;
  readonly assignedGateId: string | null;
  readonly acknowledgementState: IncidentResponderAcknowledgementState;
  readonly acknowledgementLabel: string;
  readonly blocked: boolean;
  readonly currentAction: string;
  readonly escalationPolicy: IncidentTaskEscalationKind;
  readonly lastActivityLabel: string;
  readonly tone: UiTone;
}

export interface ActiveTaskProjection {
  readonly task: IncidentTask;
  readonly owner: ResponderRow | null;
  readonly gateReady: boolean;
  readonly gateTone: UiTone;
}

export interface CommunicationEvidenceRow {
  readonly id: string;
  readonly title: string;
  readonly state: string;
  readonly tone: UiTone;
  readonly rows: readonly (readonly [string, string])[];
}

export interface AccountabilityProjection {
  readonly incidentCommander: string;
  readonly serviceOwner: string;
  readonly communicationsOwner: string;
  readonly currentAssignee: string;
  readonly handoffState: string;
  readonly ownerAcknowledgedAt: string;
}

export interface GraphNode {
  readonly kind: string;
  readonly id: string;
  readonly label: string;
  readonly roleLabel: string;
  readonly nodeKind: string;
  readonly health: GraphHealth;
  readonly column: number;
  readonly row: number;
  readonly summary: string;
  readonly badge: string;
  readonly actionCount: number;
}

export interface GraphEdge {
  readonly id: string;
  readonly from: string;
  readonly to: string;
  readonly fromLabel: string;
  readonly toLabel: string;
  readonly label: string;
  readonly displayLabel: string;
  readonly health: GraphHealth;
  readonly summary: string;
}

export interface ImpactGraph {
  readonly nodes: readonly GraphNode[];
  readonly edges: readonly GraphEdge[];
}

export interface TopologyRow {
  readonly id: string;
  readonly label: string;
  readonly health: GraphHealth;
  readonly summary: string;
  readonly meta: string;
}

export interface OpsCenterCopy {
  readonly auth: Record<string, string>;
  readonly queue: Record<string, string>;
  readonly preview: Record<string, string>;
  readonly forms: Record<string, string>;
  readonly notices: Record<string, string>;
  readonly sections: Record<string, string>;
  readonly status: Record<string, string>;
  readonly tabs: Record<TabId, string>;
  readonly standby: {
    readonly introEyebrow: string;
    readonly introTitle: string;
    readonly introBody: string;
    readonly api: string;
    readonly auth: string;
    readonly fixtureMode: string;
    readonly targetSla: string;
    readonly targetSlaValue: string;
    readonly impactTitle: string;
    readonly readiness: Record<string, readonly [string, string]>;
    readonly lanes: Record<string, readonly [string, string]>;
    readonly panels: Record<string, readonly [string, string]>;
    readonly tabPanels: Record<TabId, {
      readonly summary: string;
      readonly rows: readonly (readonly [string, string, string])[];
    }>;
  };
  readonly empty: Record<string, string>;
  readonly evidence: Record<string, string>;
  readonly actions: Record<string, string>;
  readonly actorRefs: Record<string, string>;
  readonly tasks: Record<string, string>;
  readonly orchestrator: Record<string, string>;
  readonly tools: Record<string, string>;
  readonly toolLabels: Record<string, string>;
  readonly toolProviders: Record<string, string>;
  readonly toolMeta: Record<string, string>;
  readonly serviceLabels: Record<string, string>;
  readonly routeLabels: Record<string, string>;
  readonly labels: Record<string, string>;
}
